import axios, { AxiosError, type AxiosRequestConfig } from 'axios';
import { useSession, type Person } from './session';

/** An error from the mcper.io API, with its code and a message in mcper.io's words. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }
}

type Envelope<T> = { status: number; code: string; message?: string; data?: T };
type Retriable = AxiosRequestConfig & { _retried?: boolean };

const NETWORK = 'mcper.io cannot be reached right now. Check your connection and try again.';
const FAILED = 'Something went wrong. Try again.';

export const http = axios.create({ withCredentials: true, headers: { Accept: 'application/json' } });

http.interceptors.request.use((config) => {
  const token = useSession.getState().accessToken;
  if (token) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
});

/** One refresh at a time; a 401 retries the request once with the new token. */
let refreshing: Promise<string | null> | null = null;
async function refreshToken(): Promise<string | null> {
  refreshing ??= axios
    .post<Envelope<{ accessToken: string; person: Person }>>('/auth/refresh', undefined, { withCredentials: true })
    .then((r) => {
      const d = r.data.data!;
      useSession.getState().setSession(d.accessToken, d.person);
      return d.accessToken;
    })
    .catch(() => {
      useSession.getState().clear();
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

http.interceptors.response.use(undefined, async (err: AxiosError<Envelope<unknown>>) => {
  const cfg = err.config as Retriable | undefined;
  const isSessionCall = cfg?.url?.startsWith('/auth/');
  if (err.response?.status === 401 && cfg && !cfg._retried && !isSessionCall) {
    cfg._retried = true;
    if (await refreshToken()) return http.request(cfg);
  }
  throw toApiError(err);
});

export function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  const e = err as AxiosError<Envelope<unknown>>;
  if (!e.response) return new ApiError('network', 0, NETWORK);
  const body = e.response.data;
  return new ApiError(body?.code ?? 'error', e.response.status, body?.message || FAILED);
}

function unwrap<T>(body: unknown): T {
  const env = body as Envelope<T> | undefined;
  if (env && typeof env === 'object' && 'code' in env && 'status' in env) {
    if (env.code !== 'ok') throw new ApiError(env.code, env.status, env.message || FAILED);
    return env.data as T;
  }
  return body as T;
}

export const api = {
  get: async <T>(url: string) => unwrap<T>((await http.get(url)).data),
  post: async <T>(url: string, body?: unknown) => unwrap<T>((await http.post(url, body)).data),
  put: async <T>(url: string, body?: unknown) => unwrap<T>((await http.put(url, body)).data),
  delete: async <T>(url: string) => unwrap<T>((await http.delete(url)).data),
};
